import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';
import { BsGrid3X3Gap, BsCheckCircleFill, BsExclamationTriangleFill, BsXCircleFill, BsCalendar2Check } from 'react-icons/bs';
import excelIcon from '../../assets/icons/excel.png';
import { Tooltip } from "@mui/material";
import AppDataGrid from '../../components/common/AppDataGrid';
import { fetchDataRead } from '../../Utility';
import './examschedulestatusreport.css';

const FULL = "Fully Scheduled";
const PARTIAL = "Partially Scheduled";
const NONE = "Not Scheduled";

const PILL_COLOR = {
  [FULL]: "green",
  [PARTIAL]: "amber",
  [NONE]: "red",
  "Scheduled": "green",
  "Pending": "amber",
};

const BAR_COLOR = (pct) => (pct === 100 ? "#1e8e3e" : pct === 0 ? "#d93025" : "#e37400");

const formatDate = (value) => {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${d}-${m}-${y}`;
};

const renderText = (params) => (
  <Tooltip title={params.value ?? ""}>
    <span className="essr-ellipsis">{params.value ?? ""}</span>
  </Tooltip>
);

const renderPill = (params) => (
  <span className={`essr-pill ${PILL_COLOR[params.value] || "gray"}`}>{params.value ?? ""}</span>
);

const col = (field, headerName, extra = {}) => ({
  field,
  headerName,
  flex: 1,
  minWidth: 100,
  headerAlign: "center",
  align: "center",
  sortable: true,
  renderCell: renderText,
  ...extra,
});

const SUMMARY_COLUMNS = [
  col("exam_name", "Exam", { minWidth: 100 }),
  col("class_name", "Class", { minWidth: 90 }),
  col("section_name", "Section", { minWidth: 80, flex: 0.7 }),
  col("scheduled_pct", "Scheduled", {
    flex: 1.6,
    minWidth: 170,
    renderCell: ({ row }) => (
      <div className="essr-cell-progress">
        <div className="essr-bar">
          <div className="essr-bar-fill" style={{ width: `${row.scheduled_pct}%`, background: BAR_COLOR(row.scheduled_pct) }} />
        </div>
        <span>{row.scheduled} / {row.total_subjects}</span>
      </div>
    ),
  }),
  col("not_scheduled", "Not Scheduled", { flex: 0.8 }),
  col("marks_entered", "Marks Entered", { flex: 0.8 }),
  col("status", "Status", { minWidth: 150, renderCell: renderPill }),
  col("pending_subjects", "Subjects Not Scheduled", {
    flex: 2.4,
    minWidth: 240,
    align: "left",
    renderCell: (params) => (
      <Tooltip title={params.value ?? ""}>
        <span className="essr-ellipsis essr-pending">{params.value || "-"}</span>
      </Tooltip>
    ),
  }),
];

const DETAIL_COLUMNS = [
  col("exam_name", "Exam", { minWidth: 100 }),
  col("class_name", "Class", { minWidth: 90 }),
  col("section_name", "Section", { minWidth: 80, flex: 0.7 }),
  col("subject_name", "Subject", { flex: 1.3, minWidth: 140 }),
  col("teacher_name", "Subject Teacher", { flex: 1.5, minWidth: 170 }),
  col("exam_date_display", "Exam Date"),
  col("max_marks", "Max Marks", { flex: 0.8 }),
  col("schedule_status", "Schedule", { minWidth: 130, renderCell: renderPill }),
  col("marks_status", "Marks", {
    minWidth: 120,
    renderCell: (params) => (
      params.value === "-"
        ? <span>-</span>
        : <span className={`essr-pill ${params.row.results_count > 0 ? "green" : "amber"}`}>{params.value}</span>
    ),
  }),
];

const getSectionStatus = (total, scheduled) => {
  if (scheduled === 0) return NONE;
  if (scheduled < total) return PARTIAL;
  return FULL;
};

const initialForm = { exam_id: '', class_id: '', section_id: '' };

const ExamScheduleStatusReport = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [searchedExam, setSearchedExam] = useState('');
  const [loading, setLoading] = useState(false);

  const [view, setView] = useState('summary');
  const [statusFilter, setStatusFilter] = useState('');
  const [subjectStatus, setSubjectStatus] = useState('');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchDataRead("/exammaster", setExams, userObj.school_id);
    fetchDataRead("/classes/", setClasses, userObj.school_id);
  }, []);

  useEffect(() => {
    setSections([]);
    setForm(prev => ({ ...prev, section_id: '' }));
    if (form.class_id) fetchSections(form.class_id);
  }, [form.class_id]);

  const fetchSections = async (class_id) => {
    try {
      const res = await axios.post(baseUrl + '/Sections/', {
        action: 'DROPDOWNREAD',
        class_id,
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
      });
      setSections(res.data || []);
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm(prev => ({ ...prev, [id]: value ?? '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/examschedulestatusreport/`, {
        exam_id: form.exam_id ? Number(form.exam_id) : null,
        academic_year_id: userObj.academic_year_id,
        school_id: userObj.school_id,
        class_id: form.class_id ? Number(form.class_id) : null,
        section_id: form.section_id ? Number(form.section_id) : null,
      });
      setReportData(Array.isArray(res.data) ? res.data : []);
      setSearchedExam(form.exam_id
        ? exams.find(ex => String(ex.exam_id) === String(form.exam_id))?.exam_name || 'Exam'
        : 'All Exams');
      setStatusFilter('');
      setSubjectStatus('');
    } catch (err) {
      toast.error("Something went wrong!");
      console.error("Exam Schedule Status Report Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setReportData([]);
    setSearchedExam('');
    setSearchText('');
    setStatusFilter('');
    setSubjectStatus('');
  };

  // One row per exam + class + section, with counts and the list of subjects still to be scheduled
  const summaryRows = useMemo(() => {
    const groups = new Map();
    reportData.forEach(row => {
      const key = `${row.exam_id}-${row.class_id}-${row.section_id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          exam_name: row.exam_name,
          class_name: row.class_name,
          section_name: row.section_name,
          total_subjects: 0,
          scheduled: 0,
          marks_entered: 0,
          pending: [],
        });
      }
      const g = groups.get(key);
      g.total_subjects += 1;
      if (row.schedule_status === "Scheduled") g.scheduled += 1;
      else g.pending.push(row.subject_name);
      if (row.results_count > 0) g.marks_entered += 1;
    });

    return Array.from(groups.values()).map(g => ({
      ...g,
      not_scheduled: g.total_subjects - g.scheduled,
      scheduled_pct: Math.round((g.scheduled / g.total_subjects) * 100),
      status: getSectionStatus(g.total_subjects, g.scheduled),
      pending_subjects: g.pending.join(', '),
    }));
  }, [reportData]);

  const sectionStatusByKey = useMemo(
    () => Object.fromEntries(summaryRows.map(r => [r.id, r.status])),
    [summaryRows]
  );

  const detailRows = useMemo(() => reportData.map(row => ({
    ...row,
    id: `${row.exam_id}-${row.class_id}-${row.section_id}-${row.subject_id}`,
    exam_date_display: formatDate(row.exam_date),
    section_status: sectionStatusByKey[`${row.exam_id}-${row.class_id}-${row.section_id}`],
    marks_status: row.schedule_status !== "Scheduled"
      ? "-"
      : row.results_count > 0 ? `Entered (${row.results_count})` : "Pending",
  })), [reportData, sectionStatusByKey]);

  const totals = useMemo(() => {
    const t = { sections: summaryRows.length, [FULL]: 0, [PARTIAL]: 0, [NONE]: 0 };
    summaryRows.forEach(r => { t[r.status] += 1; });
    t.subjects = reportData.length;
    t.scheduled = reportData.filter(r => r.schedule_status === "Scheduled").length;
    t.marks = reportData.filter(r => r.results_count > 0).length;
    t.pct = t.subjects ? Math.round((t.scheduled / t.subjects) * 100) : 0;
    t.exams = new Set(reportData.map(r => r.exam_id)).size;
    return t;
  }, [summaryRows, reportData]);

  const isSummary = view === 'summary';

  const filteredData = (isSummary ? summaryRows : detailRows).filter(item => {
    const sectionStatus = isSummary ? item.status : item.section_status;
    if (statusFilter && sectionStatus !== statusFilter) return false;
    if (!isSummary && subjectStatus && item.schedule_status !== subjectStatus) return false;
    return Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase());
  });

  const columns = isSummary ? SUMMARY_COLUMNS : DETAIL_COLUMNS;

  const getExportData = () => {
    const exportCols = isSummary
      ? [["exam_name", "Exam"], ["class_name", "Class"], ["section_name", "Section"], ["total_subjects", "Total Subjects"],
         ["scheduled", "Scheduled"], ["not_scheduled", "Not Scheduled"], ["marks_entered", "Marks Entered"],
         ["status", "Status"], ["pending_subjects", "Subjects Not Scheduled"]]
      : DETAIL_COLUMNS.map(c => [c.field, c.headerName]);
    return {
      headers: exportCols.map(([, h]) => h),
      rows: filteredData.map(row => exportCols.map(([f]) => row[f] ?? '')),
    };
  };

  const exportToPDF = () => {
    if (filteredData.length === 0) {
      toast.error("No data available to export.");
      return;
    }
    const { headers, rows } = getExportData();
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(`Exam Schedule Status Report - ${searchedExam}`, 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [headers],
      body: rows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });

    doc.save("Exam_Schedule_Status_Report.pdf");
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.error("No data available to export.");
      return;
    }
    const { headers, rows } = getExportData();
    const formatted = rows.map(row => Object.fromEntries(headers.map((h, i) => [h, row[i]])));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ExamScheduleStatus");
    XLSX.writeFile(wb, "Exam_Schedule_Status_Report.xlsx");
  };

  const tiles = [
    {
      key: '', cls: 'all', label: 'Total Sections', value: totals.sections,
      hint: totals.exams > 1 ? `Across ${totals.exams} exams` : `${totals.subjects} subjects`,
      icon: <BsGrid3X3Gap />,
    },
    { key: FULL, cls: 'full', label: FULL, value: totals[FULL], hint: 'All subjects scheduled', icon: <BsCheckCircleFill /> },
    { key: PARTIAL, cls: 'partial', label: PARTIAL, value: totals[PARTIAL], hint: 'Some subjects missing', icon: <BsExclamationTriangleFill /> },
    { key: NONE, cls: 'none', label: NONE, value: totals[NONE], hint: 'No subject scheduled', icon: <BsXCircleFill /> },
  ];

  const hasSearched = Boolean(searchedExam);

  return (
    <div className='pageBody'>
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body className="hide-scrollbar">
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>Exam Schedule Status Report</h6>
                  <div className="essr-subtitle">
                    Check which classes and sections have every subject scheduled for an exam.
                  </div>
                </Col>
              </Row>
              <Row className="align-items-end">
                <Col xs={12} md={6} lg={3}>
                  <Form.Group className="commonInput">
                    <Form.Label>Exam</Form.Label>
                    <Form.Select id='exam_id' value={form.exam_id} onChange={handleInputChange}>
                      <option value=''>All Exams</option>
                      {exams.map(ex => (
                        <option key={ex.exam_id} value={ex.exam_id}>{ex.exam_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Form.Group className="commonInput">
                    <Form.Label>Class</Form.Label>
                    <Form.Select id='class_id' value={form.class_id} onChange={handleInputChange}>
                      <option value=''>All Classes</option>
                      {classes.map(cls => (
                        <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <Form.Group className="commonInput">
                    <Form.Label>Section</Form.Label>
                    <Form.Select id='section_id' value={form.section_id} onChange={handleInputChange} disabled={!form.class_id}>
                      <option value=''>All Sections</option>
                      {sections.map(sec => (
                        <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className="d-flex justify-content-end mb-3">
                    <Button type="button" variant="primary" className="btn-info clearBtn me-3" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button type="submit" variant="primary" className="btn-success primaryBtn" disabled={loading}>
                      {loading ? <Spinner animation="border" size="sm" /> : "Search"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        {!hasSearched ? (
          <Card className="mt-3">
            <Card.Body className="essr-empty">
              <div className="essr-empty-icon"><BsCalendar2Check /></div>
              <h6>Choose an exam (or All Exams) and click Search</h6>
              <div>You'll see which classes and sections are fully scheduled, partially scheduled or not scheduled.</div>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row className="mt-3 g-3">
              {tiles.map(tile => (
                <Col xs={12} sm={6} xl={3} key={tile.cls}>
                  <button
                    type="button"
                    className={`essr-tile ${tile.cls} ${statusFilter === tile.key ? 'active' : ''}`}
                    onClick={() => setStatusFilter(tile.key)}
                  >
                    <span className="essr-tile-icon">{tile.icon}</span>
                    <span>
                      <div className="essr-tile-label">{tile.label}</div>
                      <div className="essr-tile-value">{tile.value}</div>
                      <div className="essr-tile-hint">{tile.hint}</div>
                    </span>
                  </button>
                </Col>
              ))}
            </Row>

            <div className="essr-progress-card mt-3">
              <div className="essr-progress-head">
                <span><strong>{searchedExam}</strong> — {totals.scheduled} of {totals.subjects} subjects scheduled ({totals.pct}%)</span>
                <span>Marks entered for {totals.marks} of {totals.scheduled} scheduled subjects</span>
              </div>
              <div className="essr-bar">
                <div className="essr-bar-fill" style={{ width: `${totals.pct}%`, background: BAR_COLOR(totals.pct) }} />
              </div>
            </div>

            <Card className="mt-3">
              <Card.Body>
                <div className="essr-toolbar">
                  <div>
                    <Button
                      variant={isSummary ? "primary" : "light"}
                      className="commonTabBtn"
                      onClick={() => setView('summary')}
                    >
                      Class / Section Summary
                    </Button>
                    <Button
                      variant={!isSummary ? "primary" : "light"}
                      className="commonTabBtn"
                      onClick={() => setView('detail')}
                    >
                      Subject Wise
                    </Button>
                  </div>
                  <div className="essr-toolbar-right">
                    {!isSummary && (
                      <Form.Select size="sm" value={subjectStatus} onChange={(e) => setSubjectStatus(e.target.value)}>
                        <option value=''>All Subjects</option>
                        <option value='Scheduled'>Scheduled</option>
                        <option value={NONE}>Not Scheduled</option>
                      </Form.Select>
                    )}
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Button variant="success" size="sm" onClick={exportToExcel} title="Export to Excel">
                      <img src={excelIcon} alt="excel" style={{ width: 18 }} />
                    </Button>
                    <Button variant="danger" size="sm" onClick={exportToPDF} title="Export to PDF">
                      <FaFilePdf />
                    </Button>
                  </div>
                </div>

                <div className="commonTable">
                  <AppDataGrid
                    rows={filteredData}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pageSizeOptions={[10, 20, 50]}
                    showToolbar={false}
                    pagination
                    density="compact"
                    maxHeight={450}
                    components={{ NoRowsOverlay: () => <div style={{ padding: 16, textAlign: 'center' }}>No Records Found</div> }}
                  />
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
    </div>
  );
};

export default ExamScheduleStatusReport;
